import { Timestamp } from "firebase/firestore";
import moment from "moment-timezone";

export function getTimestampFromYearMonthDay(
  year: number,
  month: number,
  day: number
): number {
  // Create a moment object in Montevideo timezone with the provided year, month, and day
  const montevideoTime = moment.tz(
    [year, month - 1, day],
    "America/Montevideo"
  );

  // Return the Unix timestamp
  return montevideoTime.valueOf();
}

export function getTimestamp(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
): number {
  // Create a moment object in Montevideo timezone with the provided date and time
  const montevideoTime = moment.tz(
    [year, month - 1, day, hour, minute, second],
    "America/Montevideo"
  );

  // Return the Unix timestamp
  return montevideoTime.valueOf();
}

/**
 * @param {string} dateStr En formato aaaa-mm-dd
 * @return {number} Timestamp con zona horaria Montevideo
 */
export function getTimestampFromDate(dateStr: string): number {
  // Split the date string into components
  const parts = dateStr.split("-");

  // Parse each part into a number
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Return an object with the separated values
  return getTimestampFromYearMonthDay(year, month, day);
}

/**
 * @param {string} dateStr En formato dd/mm/aaaa
 * @return {number} Timestamp con zona horaria Montevideo
 */
export function getTimestampFromDateSlash(dateStr: string): number {
  // Split the date string into components
  const parts = dateStr.split("/");

  // Parse each part into a number
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
  const year = parseInt(parts[2], 10);

  // Create a moment object with the specified date in Montevideo timezone
  const date = moment.tz({ year, month, day }, "America/Montevideo");

  // Return the timestamp (time in milliseconds since the Unix Epoch)
  return date.valueOf();
}

/**
 * @return {number} Timestamp del tiempo actual con zona horaria Montevideo
 */
export function getCurrentTime(): number {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // JavaScript months are 0-indexed
  const day = now.getDate();
  const hour = now.getHours();
  const minute = now.getMinutes();
  const second = now.getSeconds();

  return getTimestamp(year, month, day, hour, minute, second);
}

/**
 * @param {string} dateStr En formato dd/mm/aaaa
 * @return {number} Fecha como aaaa-mm-dd
 */
export function formatearFechaAAAAMMDD(fechaOriginal: string): string {
  const [dia, mes, año] = fechaOriginal.split("/");
  return `${año}-${mes}-${dia}`;
}

/**
 * Convierte un timestamp en una fecha con el formato dd/mm/aaaa.
 * @param timestamp El timestamp a convertir.
 * @returns La fecha formateada como string dd/mm/aaaa.
 */
export function formatTimestampToDate(timestamp: number): string {
  // Crea un objeto Date usando el timestamp
  const date = new Date(timestamp);

  // Obtiene el día, mes y año del objeto Date
  const day = date.getDate(); // Obtiene el día como número
  const month = date.getMonth() + 1; // Obtiene el mes como número (0-11), así que se le suma 1
  const year = date.getFullYear(); // Obtiene el año como número

  // Formatea el día y el mes para asegurar que siempre tengan dos dígitos
  const formattedDay = day < 10 ? `0${day}` : day.toString();
  const formattedMonth = month < 10 ? `0${month}` : month.toString();

  // Devuelve la fecha formateada
  return `${formattedDay}/${formattedMonth}/${year}`;
}

/**
 * @return {number} Timestamp del tiempo actual con zona horaria Montevideo
 */
export function utilFechaGetCurrentTime(): number {
  const timeZone = "America/Montevideo";
  const nowInMontevideo = moment().tz(timeZone);
  return nowInMontevideo.valueOf();
}

export function getFechaActualMontevideo() {
  // Crear un objeto Date con la hora actual
  const date = new Date();

  // Convertir la hora actual a la zona horaria de Montevideo
  const options: any = {
    timeZone: "America/Montevideo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  };
  const fechaFormateada = date.toLocaleDateString("es-ES", options);

  // Retornar la fecha formateada en el formato dd/mm/aaaa
  return fechaFormateada;
}

/**
 * Convierte un timestamp en una fecha con el formato dd/mm/aaaa hh:mm:ss.
 * @param timestamp El timestamp a convertir.
 * @returns La fecha formateada como string dd/mm/aaaa hh:mm:ss.
 */
export function formatTimestampToDateExact(timestamp: number): string {
  // Crea un objeto Date usando el timestamp
  const date = new Date(timestamp);

  // Obtiene el día, mes, año, hora, minutos y segundos del objeto Date
  const day = date.getDate(); // Obtiene el día como número
  const month = date.getMonth() + 1; // Obtiene el mes como número (0-11), así que se le suma 1
  const year = date.getFullYear(); // Obtiene el año como número
  const hours = date.getHours(); // Obtiene la hora como número
  const minutes = date.getMinutes(); // Obtiene los minutos como número
  const seconds = date.getSeconds(); // Obtiene los segundos como número

  // Formatea el día, mes, hora, minutos y segundos para asegurar que siempre tengan dos dígitos
  const formattedDay = day < 10 ? `0${day}` : day.toString();
  const formattedMonth = month < 10 ? `0${month}` : month.toString();
  const formattedHours = hours < 10 ? `0${hours}` : hours.toString();
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes.toString();
  const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds.toString();

  // Devuelve la fecha y la hora formateadas
  return `${formattedDay}/${formattedMonth}/${year} ${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export function validarFecha(fecha: string) {
  if (fecha === "" || fecha === "null") {
    return true;
  }
  const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
  if (!regex.test(fecha)) {
    return false;
  }
  const [day, month, year] = fecha.split("/").map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function formatearFechaTimestampDDMMYYYY(
  fecha: Timestamp | Date | number,
  conHora: boolean = false
): string {
  let date: Date;

  if (fecha instanceof Date) {
    date = fecha;
  } else if (typeof fecha === "number") {
    date = new Date(fecha);
  } else {
    // Timestamp de Firestore
    date = fecha.toDate();
  }

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();

  if (!conHora) {
    return `${dd}/${mm}/${yyyy}`;
  }

  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const seg = String(date.getSeconds()).padStart(2, "0");
  const mil = String(date.getMilliseconds()).padStart(2, "0");

  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}
export const getCurrentDate = () => {
  const date = new Date();
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const devolverHoraTimestamp = (fecha: number) => {
  let fechaTimestamp = new Timestamp(fecha, 0);
  return fechaTimestamp
    .toDate()
    .toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
};
